import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, WooCommerceIntegrationTimestamps as WooCommerceIntegrationTimestampsType } from '@/types';
import { apiService } from '@/utils/api';

const WooCommerceIntegrationTimestamps: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    order_create_latest_poll_time: '',
    order_update_latest_poll_time: '',
    product_create_latest_poll_time: '',
    product_update_latest_poll_time: ''
  });
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const queryClient = useQueryClient();

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const sitesData = await apiService.getSites();
        setSites(sitesData);
        if (sitesData.length > 0) {
          setSelectedSiteId(sitesData[0].id);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };
    fetchSites();
  }, []);

  // Fetch WooCommerce integration timestamps
  const { data: timestampsData, isLoading } = useQuery<WooCommerceIntegrationTimestampsType>(
    ['woocommerce-integration-timestamps', selectedSiteId],
    () => apiService.getWooCommerceIntegrationTimestamp(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
      onSuccess: (data: WooCommerceIntegrationTimestampsType) => {
        setFormData({
          site_id: data.site_id?.toString() || '',
          order_create_latest_poll_time: data.order_create_latest_poll_time ? 
            new Date(data.order_create_latest_poll_time).toISOString().slice(0, 16) : '',
          order_update_latest_poll_time: data.order_update_latest_poll_time ? 
            new Date(data.order_update_latest_poll_time).toISOString().slice(0, 16) : '',
          product_create_latest_poll_time: data.product_create_latest_poll_time ? 
            new Date(data.product_create_latest_poll_time).toISOString().slice(0, 16) : '',
          product_update_latest_poll_time: data.product_update_latest_poll_time ? 
            new Date(data.product_update_latest_poll_time).toISOString().slice(0, 16) : ''
        });
      },
      onError: () => {
        // Reset form if no data found
        setFormData({
          site_id: selectedSiteId?.toString() || '',
          order_create_latest_poll_time: '',
          order_update_latest_poll_time: '',
          product_create_latest_poll_time: '',
          product_update_latest_poll_time: ''
        });
      }
    }
  );

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: WooCommerceIntegrationTimestampsType) => {
      if (timestampsData?.id) {
        return apiService.updateWooCommerceIntegrationTimestamps(selectedSiteId!, data);
      } else {
        return apiService.createWooCommerceIntegrationTimestamps(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setShowSuccess(true);
        queryClient.invalidateQueries(['woocommerce-integration-timestamps', selectedSiteId]);
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteWooCommerceIntegrationTimestamps(selectedSiteId!),
    {
      onSuccess: () => {
        setShowDeleteSuccess(true);
        setFormData({
          site_id: selectedSiteId?.toString() || '',
          order_create_latest_poll_time: '',
          order_update_latest_poll_time: '',
          product_create_latest_poll_time: '',
          product_update_latest_poll_time: ''
        });
        queryClient.invalidateQueries(['woocommerce-integration-timestamps', selectedSiteId]);
        setTimeout(() => setShowDeleteSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: WooCommerceIntegrationTimestampsType = {
      ...formData,
      site_id: parseInt(formData.site_id),
      order_create_latest_poll_time: formData.order_create_latest_poll_time ? 
        new Date(formData.order_create_latest_poll_time).toISOString() : undefined,
      order_update_latest_poll_time: formData.order_update_latest_poll_time ? 
        new Date(formData.order_update_latest_poll_time).toISOString() : undefined,
      product_create_latest_poll_time: formData.product_create_latest_poll_time ? 
        new Date(formData.product_create_latest_poll_time).toISOString() : undefined,
      product_update_latest_poll_time: formData.product_update_latest_poll_time ? 
        new Date(formData.product_update_latest_poll_time).toISOString() : undefined
    };
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these timestamps?')) {
      deleteMutation.mutate();
    }
  };

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSiteId = parseInt(e.target.value);
    setSelectedSiteId(newSiteId);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">WooCommerce Integration Timestamps</h1>

      {/* Success Messages */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                WooCommerce Integration Timestamps saved successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {showDeleteSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                WooCommerce Integration Timestamps deleted successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Something went wrong. Please try again.
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Site Selection */}
          <div>
            <label htmlFor="site_id" className="block text-sm font-medium text-gray-700 mb-2">
              Site
            </label>
            <select
              id="site_id"
              name="site_id"
              value={selectedSiteId || ''}
              onChange={handleSiteChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Order Create Latest Poll Time */}
          <div>
            <label htmlFor="order_create_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time Create Orders
            </label>
            <input
              type="datetime-local"
              id="order_create_latest_poll_time"
              name="order_create_latest_poll_time"
              value={formData.order_create_latest_poll_time}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Order Update Latest Poll Time */}
          <div>
            <label htmlFor="order_update_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time Update Orders
            </label>
            <input
              type="datetime-local"
              id="order_update_latest_poll_time"
              name="order_update_latest_poll_time"
              value={formData.order_update_latest_poll_time}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Product Create Latest Poll Time */}
          <div>
            <label htmlFor="product_create_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time Create Products
            </label>
            <input
              type="datetime-local"
              id="product_create_latest_poll_time"
              name="product_create_latest_poll_time"
              value={formData.product_create_latest_poll_time}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Product Update Latest Poll Time */}
          <div>
            <label htmlFor="product_update_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time Update Products
            </label>
            <input
              type="datetime-local"
              id="product_update_latest_poll_time"
              name="product_update_latest_poll_time"
              value={formData.product_update_latest_poll_time}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save WooCommerce Integration Timestamps'}
            </button>

            {timestampsData?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default WooCommerceIntegrationTimestamps;
